import { combineReducers } from 'redux';

import entities, * as fromEntities from './entities';
import entityCollections, * as fromEntityCollections from './entityCollections';
import features, * as fromFeatures from './features';

import type { CurrentFlightItineraryIdsState } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entityCollections/currentFlightItineraryIds';
import type { Alliance } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/alliances';
import type {
  Carrier,
  CarrierMultipleAggregate,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/carriers';
import type {
  FilterStatusMap,
  Itinerary,
  ItineraryById,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/itineraries';
import type { Leg } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/legs';
import type { Segment } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/segments';
import type { RailResultsState } from '@web-platform/shared-universal-types/src/redux/state/entities/rail';
import type { AlliancesFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/alliances';
import type { BaggageFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/baggage';
import type { CarriersFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/carriers';
import type { DepartureTimesState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/departureTimes';
import type { DurationFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/duration';
import type { EcoFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/eco';
import type { SelectedHotelsFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/hotelsFilter';
import type { MixedAirportFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/mixedAirport';
import type { FiltersState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/state';
import type { StopsFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/stops';
import type { SingleLegResultsState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/singleLegResults/singleLegResults';
import type { FlightSearchResultsState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/flightSearchResults';
import type { PartnerMetadataTrustLevel } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/partnerMetadata/partnerMetadata';
import type { SelectedSortState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/sort/sort';
import type { Place } from '@web-platform/shared-universal-types/src/redux/state/places/places';
import type { PriceBandsState } from '@web-platform/shared-universal-types/src/redux/state/priceBands/priceBands';
import type { Agent } from '@web-platform/shared-universal-types/src/redux/state/shared/flightSearchResults/agents';
import type { EntityId } from '@web-platform/shared-universal-types-utils/src/utils';

const flightSearchResultsReducer = combineReducers({
  entities,
  entityCollections,
  features,
});

export default flightSearchResultsReducer;

// entities - agents
export const agent = (state: FlightSearchResultsState, id: string): Agent | undefined =>
  fromEntities.agent(state.entities, id);
export const allAgents = (state: FlightSearchResultsState): Record<string, Agent> =>
  fromEntities.allAgents(state.entities);

// entities - flights
export const flightLeg = (state: FlightSearchResultsState, id: string): Leg | undefined =>
  fromEntities.flightLeg(state.entities, id);
export const flightAllLegs = (state: FlightSearchResultsState): Record<string, Leg> =>
  fromEntities.flightAllLegs(state.entities);
export const flightSegment = (state: FlightSearchResultsState, id: string): Segment | undefined =>
  fromEntities.flightSegment(state.entities, id);
export const flightAllSegments = (state: FlightSearchResultsState): Record<string, Segment> =>
  fromEntities.flightAllSegments(state.entities);
export const flightAlliance = (state: FlightSearchResultsState, id: number): Alliance | undefined =>
  fromEntities.flightAlliance(state.entities, id);
export const flightAllAlliances = (state: FlightSearchResultsState): Record<number, Alliance> =>
  fromEntities.flightAllAlliances(state.entities);
export const flightCarrier = (state: FlightSearchResultsState, id: number): Carrier | undefined =>
  fromEntities.flightCarrier(state.entities, id);
export const flightAllCarriers = (state: FlightSearchResultsState): Record<number, Carrier> =>
  fromEntities.flightAllCarriers(state.entities);
export const flightCarriersMultiple = (
  state: FlightSearchResultsState,
): CarrierMultipleAggregate | null => fromEntities.flightCarriersMultiple(state.entities);
export const flightCarriersCurrentResultIds = (state: FlightSearchResultsState): number[] =>
  fromEntities.flightCarriersCurrentResultIds(state.entities);
export const flightItinerary = (state: FlightSearchResultsState, id: string): Itinerary =>
  fromEntities.flightItinerary(state.entities, id);
export const flightAllItineraries = (state: FlightSearchResultsState): ItineraryById =>
  fromEntities.flightAllItineraries(state.entities);
export const getItineraryFilterStatusMap = (
  state: FlightSearchResultsState,
  id: string,
): FilterStatusMap[] => fromEntities.getItineraryFilterStatusMap(state.entities, id);

// entityCollections
export const getCurrentFlightItineraryIds = (
  state: FlightSearchResultsState,
): CurrentFlightItineraryIdsState =>
  fromEntityCollections.getCurrentFlightItineraryIds(state.entityCollections);

// entities - places

export const flightPlaceByEntityId = (state: FlightSearchResultsState, entityId: EntityId): Place =>
  fromEntities.flightPlaceByEntityId(state.entities, entityId);

export const flightAllPlacesByEntityId = (
  state: FlightSearchResultsState,
): Record<EntityId, Place> => fromEntities.flightAllPlacesByEntityId(state.entities);

// sort
export const getSelectedSort = (state: FlightSearchResultsState): SelectedSortState =>
  fromFeatures.getSelectedSort(state.features);

// partnerMetadata
export const getPartnerMetadata = (
  state: FlightSearchResultsState,
  id: string,
): PartnerMetadataTrustLevel => fromFeatures.getPartnerMetadata(state.features, id);
export const getAllPartnerMetadata = (
  state: FlightSearchResultsState,
): Record<string, PartnerMetadataTrustLevel> => fromFeatures.getAllPartnerMetadata(state.features);

// rail
export const getRail = (state: FlightSearchResultsState): RailResultsState =>
  fromFeatures.getRail(state.features);

// pricePulse
export const getPricePulse = (state: FlightSearchResultsState, itineraryId: string) =>
  fromFeatures.getPricePulse(state.features, itineraryId);

export const getPricePulseByIdMap = (state: FlightSearchResultsState) =>
  fromFeatures.getPricePulseByIdMap(state.features);

// alternativeDates
export const getAlternativeDates = (state: FlightSearchResultsState) =>
  fromFeatures.getAlternativeDates(state.features);

// filters
export const getFilters = (state: FlightSearchResultsState): FiltersState =>
  fromFeatures.getFilters(state.features);
export const getStopsFilter = (state: FlightSearchResultsState): StopsFilterState =>
  fromFeatures.getStopsFilter(state.features);
export const getAirportsFilter = (state: FlightSearchResultsState) =>
  fromFeatures.getAirportsFilter(state.features);
export const getAirportsStatsFilter = (state: FlightSearchResultsState) =>
  fromFeatures.getAirportsStatsFilter(state.features);
export const getFilterOrSortChangedCount = (state: FiltersState) => state.filterOrSortChangedCount;
export const getLayoversDurationFilter = (state: FlightSearchResultsState) =>
  fromFeatures.getLayoversDurationFilter(state.features);
export const getLayoversExcludedAirportsFilter = (state: FlightSearchResultsState) =>
  fromFeatures.getLayoversExcludedAirportsFilter(state.features);
export const getDepartureTimesFilter = (state: FlightSearchResultsState): DepartureTimesState =>
  fromFeatures.getDepartureTimesFilter(state.features);
export const getDurationFilter = (state: FlightSearchResultsState): DurationFilterState =>
  fromFeatures.getDurationFilter(state.features);
export const getCarriersFilter = (state: FlightSearchResultsState): CarriersFilterState =>
  fromFeatures.getCarriersFilter(state.features);
export const getAlliancesFilter = (state: FlightSearchResultsState): AlliancesFilterState =>
  fromFeatures.getAlliancesFilter(state.features);
export const getMixedAirportFilter = (state: FlightSearchResultsState): MixedAirportFilterState =>
  fromFeatures.getMixedAirportFilter(state.features);
export const getHotelsFilter = (state: FlightSearchResultsState): SelectedHotelsFilterState =>
  fromFeatures.getHotelsFilter(state.features);
export const getEcoFilter = (state: FlightSearchResultsState): EcoFilterState =>
  fromFeatures.getEcoFilter(state.features);
export const getBaggageFilter = (state: FlightSearchResultsState): BaggageFilterState =>
  fromFeatures.getBaggageFilter(state.features);

// single leg results
export const getSingleLegResults = (state: FlightSearchResultsState): SingleLegResultsState =>
  fromFeatures.getSingleLegResults(state.features);

// priceBands
export const getPriceBands = (state: FlightSearchResultsState): PriceBandsState =>
  fromFeatures.getPriceBands(state.features);
